'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import { offlineDB, type OfflineVerification, type OfflineBoothInfo } from '@/lib/offline-db';
import { useOfflineSync } from '@/hooks/use-offline-sync';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  ClipboardList,
  Printer,
  Loader2,
  ArrowLeft,
  Users,
  AlertCircle,
} from 'lucide-react';

export function Report17A() {
  const { token, user } = useAuthStore();
  const { setCurrentView, isOnline } = useAppStore();
  const { pendingCount } = useOfflineSync();

  const [boothInfo, setBoothInfo] = useState<OfflineBoothInfo | null>(null);
  const [verifications, setVerifications] = useState<OfflineVerification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [reportDate, setReportDate] = useState('');

  const boothId = user?.boothId || '';

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await offlineDB.init();
        const info = await offlineDB.getBoothInfo(boothId);
        if (info) setBoothInfo(info);
        const localVerifications = await offlineDB.getVerifications(boothId);
        setVerifications(localVerifications);

        if (isOnline && token) {
          try {
            const res = await fetch(`/api/reports?boothId=${boothId}&type=17a`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
              const data = await res.json();
              if (data.success && data.booth) {
                setBoothInfo((prev) => prev || {
                  pollingBoothId: data.booth.pollingBoothId,
                  name: data.booth.name,
                  address: data.booth.address || '',
                  district: data.booth.district || '',
                  constituency: data.booth.constituency || '',
                  constituencyNo: data.booth.constituencyNo || '',
                  wardNo: data.booth.wardNo || '',
                  totalVoters: data.booth.totalVoters || 0,
                  downloadedAt: new Date().toISOString(),
                });
              }
            }
          } catch { /* offline */ }
        }

        const now = new Date();
        setReportDate(now.toLocaleDateString('hi-IN', {
          day: '2-digit', month: 'long', year: 'numeric',
        }));
      } catch (error) {
        console.error('Failed to load report data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    if (boothId) loadData();
  }, [boothId, isOnline, token]);

  const downloadReport = useCallback(() => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // Build table rows from verifications data
    const tableRows = verifications.map((v, index) => {
      // Column 3: voter details
      const voterDetails = [
        `<strong>${v.voterName}</strong>`,
        `EPiC: ${v.epicNo}`,
        `${v.relationType}: ${v.fatherHusbandName}`,
        `${v.gender} | ${v.age} वर्ष`,
      ].join('<br/>');

      // Column 4: signature/thumb image
      let sigContent = '—';
      if (v.verificationType === 'signature' && v.signatureImage) {
        sigContent = `<img src="${v.signatureImage}" alt="हस्ताक्षर" style="max-width:40px;max-height:24px;display:block;margin:0 auto;object-fit:contain;" />`;
      } else if (v.thumbImage) {
        sigContent = `<img src="${v.thumbImage}" alt="अंगूठे का निशान" style="max-width:40px;max-height:24px;display:block;margin:0 auto;object-fit:contain;" />`;
      }

      // Column 5: remarks
      const remark = v.synced === 0 ? 'लंबित सिंक' : '';

      return `<tr>
        <td style="text-align:center;">${index + 1}</td>
        <td style="text-align:center;">${v.serialNo}</td>
        <td style="text-align:left;font-size:10px;line-height:1.4;">${voterDetails}</td>
        <td style="text-align:center;">${sigContent}</td>
        <td style="text-align:center;">${remark}</td>
      </tr>`;
    }).join('\n');

    // Build the complete HTML from scratch — NO DOM innerHTML
    printWindow.document.write(`<!DOCTYPE html>
<html lang="hi">
<head>
  <meta charset="UTF-8" />
  <title>फॉर्म-17अ | Form 17-A</title>
  <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;500;600;700&display=swap" rel="stylesheet" />
  <style>
    @page {
      size: A4;
      margin: 12mm 15mm;
    }
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: 'Noto Sans Devanagari', Arial, sans-serif;
      color: #000;
      background: #fff;
      font-size: 12px;
      line-height: 1.6;
      padding: 20px;
    }
    .form-container {
      max-width: 780px;
      margin: 0 auto;
    }

    /* === HEADER SECTION === */
    .header {
      text-align: center;
      margin-bottom: 14px;
      padding-bottom: 10px;
      border-bottom: 3px double #000;
    }
    .annexure {
      font-size: 13px;
      font-weight: 600;
      margin-bottom: 4px;
    }
    .form-title {
      font-size: 20px;
      font-weight: 700;
      margin-bottom: 2px;
    }
    .legal-ref {
      font-size: 11px;
      color: #333;
      margin-bottom: 4px;
    }
    .form-subtitle {
      font-size: 15px;
      font-weight: 600;
      margin-bottom: 8px;
    }
    .body-info {
      text-align: center;
      font-size: 12px;
      line-height: 2.2;
    }
    .body-info .blank {
      display: inline-block;
      min-width: 180px;
      border-bottom: 1px solid #000;
      margin: 0 4px;
      font-weight: 600;
    }
    .booth-row {
      text-align: center;
      font-size: 12px;
      margin: 4px 0;
      line-height: 1.8;
    }
    .booth-row .label { color: #444; }
    .booth-row .value { font-weight: 600; }
    .booth-sep {
      margin: 0 16px;
    }

    /* === TABLE === */
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 14px;
    }
    th, td {
      border: 1px solid #000;
      padding: 6px 5px;
      font-size: 11px;
      vertical-align: middle;
      text-align: center;
      color: #000;
    }
    th {
      background: #f5f5f5;
      font-weight: 600;
      font-size: 10px;
      line-height: 1.4;
    }

    /* === FOOTER === */
    .date-field {
      margin-top: 24px;
      font-size: 12px;
    }
    .date-field .blank {
      display: inline-block;
      min-width: 160px;
      border-bottom: 1px solid #000;
      margin-left: 4px;
    }
    .footer-section {
      margin-top: 36px;
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      font-size: 12px;
    }
    .footer-left {
      line-height: 1.8;
    }
    .sign-line {
      border-top: 1px solid #000;
      width: 220px;
      padding-top: 4px;
      text-align: center;
      font-weight: 500;
    }

    /* === PRINT === */
    @media print {
      body {
        padding: 0;
      }
      .form-container {
        max-width: 100%;
      }
      th {
        background: #f5f5f5 !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
  </style>
</head>
<body>
  <div class="form-container">
    <!-- HEADER -->
    <div class="header">
      <p class="annexure">परिशिष्ट-चार</p>
      <h1 class="form-title">फॉर्म-17अ</h1>
      <p class="legal-ref">(नियम 63ई के उपनियम (1अ) के अनुसार)</p>
      <p class="form-subtitle">मतदाता रजिस्टर</p>

      <div class="body-info">
        <p>नगर निगम/नगर पालिका परिषद/नगर परिषद <span class="blank">${boothInfo?.name || ''}</span> के</p>
        <p>अध्यक्ष/अध्यक्ष का चुनाव तथा वार्ड क्रमांक <span class="blank" style="min-width:120px;">${boothInfo?.wardNo || ''}</span> से व्यक्ति का चुनाव</p>
      </div>

      <div class="booth-row">
        <span class="label">निर्वाचन क्षेत्र न.:</span> <span class="value">${boothInfo?.constituencyNo || '___'}</span>
        <span class="booth-sep"></span>
        <span class="label">निर्वाचन क्षेत्र:</span> <span class="value">${boothInfo?.constituency || '___'}</span>
        <span class="booth-sep"></span>
        <span class="label">जिला:</span> <span class="value">${boothInfo?.district || '___'}</span>
      </div>
    </div>

    <!-- TABLE -->
    <table>
      <thead>
        <tr>
          <th style="width:45px;">क्रमांक<br/>(1)</th>
          <th style="width:70px;">मतदाता सूची में<br/>मतदाता का<br/>क्रमांक<br/>(2)</th>
          <th style="min-width:160px;">मतदाता द्वारा अपनी<br/>पहचान के सम्बन्ध में<br/>प्रस्तुत दस्तावेज<br/>का विवरण<br/>(3)</th>
          <th style="width:100px;">मतदाता के<br/>हस्ताक्षर/<br/>अंगूठे का निशान<br/>(4)</th>
          <th style="width:70px;">टिप्पणी<br/>(5)</th>
        </tr>
      </thead>
      <tbody>
        ${tableRows}
      </tbody>
    </table>

    <!-- DATE -->
    <div class="date-field">
      दिनांक <span class="blank">${reportDate}</span>
    </div>

    <!-- FOOTER -->
    <div class="footer-section">
      <div class="footer-left">
        <div>कुल सत्यापित: <strong>${verifications.length}</strong></div>
        <div>पुरुष: ${verifications.filter(v => v.gender === 'पुरुष').length} | महिला: ${verifications.filter(v => v.gender === 'महिला').length}</div>
      </div>
      <div style="text-align:center;">
        <div class="sign-line">पीठासीन अधिकारी का हस्ताक्षर</div>
      </div>
    </div>
  </div>
</body>
</html>`);

    printWindow.document.close();

    // Delay 500ms to allow Google Font to load before printing
    setTimeout(() => {
      printWindow.print();
    }, 500);
  }, [boothInfo, verifications, reportDate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-3">
          <Loader2 className="w-10 h-10 text-emerald-600 animate-spin mx-auto" />
          <p className="text-emerald-800 font-medium">रिपोर्ट लोड हो रही है...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-4 space-y-4">
      {/* Header Bar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-6 h-6 text-emerald-700" />
          <div>
            <h2 className="text-lg font-bold text-emerald-900">फॉर्म-17अ</h2>
            <p className="text-xs text-gray-500">मतदाता रजिस्टर | Voter Register</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50">
            <Users className="w-3 h-3 mr-1" />
            {verifications.length} सत्यापित
          </Badge>
          {pendingCount > 0 && (
            <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">
              {pendingCount} लंबित सिंक
            </Badge>
          )}
          <Button onClick={downloadReport} className="bg-emerald-600 hover:bg-emerald-700 text-white min-h-[44px]">
            <Printer className="w-4 h-4 mr-2" />
            प्रिंट / डाउनलोड
          </Button>
        </div>
      </div>

      {/* Report Content - Exact Form 17-A Layout */}
      <Card className="border-emerald-200 shadow-md" id="report-17a-content">
        <CardContent className="p-6">
          {/* ===== HEADER SECTION - Exact PDF Format ===== */}
          <div className="form-container text-center mb-4 pb-4 border-b-[3px] border-double border-black">
            {/* परिशिष्ट-चार */}
            <p className="text-sm font-semibold text-gray-800 mb-1">परिशिष्ट-चार</p>

            {/* फॉर्म-17अ */}
            <h1 className="text-xl font-bold text-black mb-1">फॉर्म-17अ</h1>

            {/* Legal reference */}
            <p className="text-xs text-gray-700 mb-2">
              (नियम 63ई के उपनियम (1अ) के अनुसार)
            </p>

            {/* मतदाता रजिस्टर */}
            <p className="text-sm font-semibold text-black mb-3">मतदाता रजिस्टर</p>

            {/* Body type & Election info - Exact PDF layout */}
            <div className="body-info text-xs leading-7">
              <p>
                नगर निगम/नगर पालिका परिषद/नगर परिषद{' '}
                <span className="inline-block min-w-[180px] border-b border-black mx-1">{boothInfo?.name || ''}</span>
                {' '}के
              </p>
              <p>
                अध्यक्ष/अध्यक्ष का चुनाव तथा वार्ड क्रमांक{' '}
                <span className="inline-block min-w-[120px] border-b border-black mx-1">{boothInfo?.wardNo || ''}</span>
                {' '}से व्यक्ति का चुनाव
              </p>
            </div>

            {/* Booth Details Row */}
            <div className="flex items-center justify-center gap-6 text-xs flex-wrap mt-2">
              <span>
                <span className="text-gray-600">निर्वाचन क्षेत्र न.:</span>{' '}
                <span className="font-semibold">{boothInfo?.constituencyNo || '___'}</span>
              </span>
              <span>
                <span className="text-gray-600">निर्वाचन क्षेत्र:</span>{' '}
                <span className="font-semibold">{boothInfo?.constituency || '___'}</span>
              </span>
              <span>
                <span className="text-gray-600">जिला:</span>{' '}
                <span className="font-semibold">{boothInfo?.district || '___'}</span>
              </span>
            </div>
          </div>

          {/* Verifications Table - Exact PDF column headers */}
          {verifications.length === 0 ? (
            <div className="text-center py-16">
              <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">कोई सत्यापन रिकॉर्ड नहीं मिला</p>
              <p className="text-gray-400 text-sm mt-1">मतदाता सत्यापन के बाद यह रिपोर्ट उपलब्ध होगी</p>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[55vh] overflow-y-auto styled-scrollbar">
              <Table className="border border-black">
                <TableHeader>
                  <TableRow className="bg-gray-100 hover:bg-gray-100">
                    <TableHead className="border border-black text-center font-bold w-[50px] text-xs">
                      क्रमांक<br />(1)
                    </TableHead>
                    <TableHead className="border border-black text-center font-bold w-[80px] text-xs">
                      मतदाता सूची में<br />मतदाता का<br />क्रमांक<br />(2)
                    </TableHead>
                    <TableHead className="border border-black text-center font-bold text-xs min-w-[160px]">
                      मतदाता द्वारा अपनी<br />पहचान के सम्बन्ध में<br />प्रस्तुत दस्तावेज<br />का विवरण<br />(3)
                    </TableHead>
                    <TableHead className="border border-black text-center font-bold w-[110px] text-xs">
                      मतदाता के<br />हस्ताक्षर/<br />अंगूठे का निशान<br />(4)
                    </TableHead>
                    <TableHead className="border border-black text-center font-bold w-[80px] text-xs">
                      टिप्पणी<br />(5)
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {verifications.map((v, index) => (
                    <TableRow key={v.id} className="hover:bg-gray-50/50 even:bg-gray-50/30">
                      <TableCell className="border border-black/30 text-center text-xs font-medium">
                        {index + 1}
                      </TableCell>
                      <TableCell className="border border-black/30 text-center text-xs">
                        {v.serialNo}
                      </TableCell>
                      <TableCell className="border border-black/30 text-xs">
                        <div className="space-y-0.5">
                          <p className="font-medium">{v.voterName}</p>
                          <p className="text-gray-500">EPiC: <span className="font-mono">{v.epicNo}</span></p>
                          <p className="text-gray-500">{v.relationType}: {v.fatherHusbandName}</p>
                          <p className="text-gray-500">{v.gender} | {v.age} वर्ष</p>
                        </div>
                      </TableCell>
                      <TableCell className="border border-black/30 text-center">
                        {v.verificationType === 'signature' && v.signatureImage ? (
                          <img src={v.signatureImage} alt="हस्ताक्षर" className="max-w-[40px] max-h-[24px] mx-auto object-contain" />
                        ) : v.thumbImage ? (
                          <img src={v.thumbImage} alt="अंगूठे का निशान" className="max-w-[40px] max-h-[24px] mx-auto object-contain" />
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="border border-black/30 text-center text-xs text-gray-500">
                        {v.synced === 0 ? 'लंबित सिंक' : ''}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Date Field - Exact PDF format */}
          <div className="mt-5 text-xs">
            <span className="font-medium">दिनांक</span>{' '}
            <span className="inline-block min-w-[140px] border-b border-black ml-1">{reportDate}</span>
          </div>

          {/* Footer / Signature - Exact PDF format */}
          <div className="mt-6 flex items-end justify-between text-xs">
            <div>
              <p className="text-gray-500">कुल सत्यापित: <span className="font-bold text-black">{verifications.length}</span></p>
              <p className="text-gray-500">पुरुष: {verifications.filter(v => v.gender === 'पुरुष').length} | महिला: {verifications.filter(v => v.gender === 'महिला').length}</p>
            </div>
            <div className="text-center">
              <div className="w-48 border-t border-black pt-1.5 mt-6">
                <p className="font-medium">पीठासीन अधिकारी का हस्ताक्षर</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Back Button */}
      <div className="flex justify-center pt-2 pb-4">
        <Button variant="outline" onClick={() => setCurrentView('officer-dashboard')} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 min-h-[44px]">
          <ArrowLeft className="w-4 h-4 mr-2" />
          वापस जाएं
        </Button>
      </div>
    </div>
  );
}
